import { useErrorHandlers } from "@/errors/components/error-handling-context";
import { useCurrentArea, useCurrentScene } from "@/modes/mode-data-context";
import { selectComputePointcloud } from "@/modes/walk-mode/walk-scene-selectors";
import {
  useAppDispatch,
  useAppSelector,
  useAppStore,
} from "@/store/store-hooks";
import { selectCurrentUser } from "@/store/user-selectors";
import { useBoxControlsClippingPlanes } from "@/utils/box-controls-context";
import { planesToArray } from "@/utils/planes-to-array";
import { useToast } from "@faro-lotv/flat-ui";
import { isIElementPointCloudStream } from "@faro-lotv/ielement-types";
import { assert } from "@faro-lotv/lotv";
import { selectProjectId } from "@faro-lotv/project-source";
import {
  OrthoPhotoGenerationLimitsResponse,
  OrthoPhotoGenerationRequest,
  useApiClientContext,
} from "@faro-lotv/service-wires";
import { useCallback, useEffect, useState } from "react";
import { returnToPreviousMode } from "../export-mode-utils";

/** Default orthophoto generation limits if the request fetch the latest limits fails */
const DEFAULT_ORTHOPHOTO_LIMITS: OrthoPhotoGenerationLimitsResponse = {
  resolutionMin: 0.001,
  resolutionMax: 5.0,
  sizeMin: 200,
  sizeMax: 30000,
  pixelsMax: 900000000,
  widthMin: 200,
};

interface UseOrthophotoBackendResult {
  /** The current orthophoto generation limits */
  orthophotoLimits: OrthoPhotoGenerationLimitsResponse;

  /** A callback to start an orthophoto generation */
  generateOrthophoto(
    fileName: string,
    resolution: number,
    plane: number,
  ): Promise<void>;
}

/**
 * @returns A callback to start an orthophoto generation
 */
export function useOrthophotoBackend(): UseOrthophotoBackendResult {
  const projectId = useAppSelector(selectProjectId);
  const user = useAppSelector(selectCurrentUser);
  const currentScene = useCurrentScene();
  const currentArea = useCurrentArea();

  const store = useAppStore();
  const dispatch = useAppDispatch();

  assert(projectId, "Project ID is not available");
  assert(user, "User is not available");

  const { pointCloudApiClient } = useApiClientContext();

  const pointCloud = useAppSelector(
    selectComputePointcloud(currentScene, currentArea),
  );

  const main =
    currentScene.main && isIElementPointCloudStream(currentScene.main)
      ? currentScene.main
      : pointCloud;

  assert(
    main && isIElementPointCloudStream(main),
    "The overview image preview requires a point cloud",
  );

  const { handleErrorWithToast } = useErrorHandlers();
  const { openToast } = useToast();

  const [orthophotoLimits, setOrthophotoLimits] = useState(
    DEFAULT_ORTHOPHOTO_LIMITS,
  );

  // Fetch the orthophoto generation limits
  useEffect(() => {
    const ac = new AbortController();
    pointCloudApiClient
      .getOrthophotoGenerationLimits(ac.signal)
      .then(setOrthophotoLimits);

    return () => ac.abort();
  }, [pointCloudApiClient]);

  const clippingPlanesBox = useBoxControlsClippingPlanes();

  const generateOrthophoto = useCallback(
    async (fileName: string, resolution: number, plane: number) => {
      assert(clippingPlanesBox, "Clipping box is not available");

      try {
        const orthophotoGenerationPayload: OrthoPhotoGenerationRequest = {
          projectId,
          userId: user.id,
          elementId: main.id,
          fileName,
          volume: planesToArray(clippingPlanesBox),
          resolution,
          plane,
        };

        await pointCloudApiClient.generateOrthophoto(
          orthophotoGenerationPayload,
        );
        openToast({
          title: "Preparing data for orthophoto generation",
          message:
            "You can track the orthophoto generation progress in Cloud activity and download the file once it's ready.",
        });
        returnToPreviousMode(store, dispatch);
      } catch {
        handleErrorWithToast({
          title: "The orthophoto generation failed",
          error: "Something went wrong. Try again later.",
        });
      }
    },
    [
      clippingPlanesBox,
      dispatch,
      handleErrorWithToast,
      main.id,
      openToast,
      pointCloudApiClient,
      projectId,
      store,
      user.id,
    ],
  );

  return { orthophotoLimits, generateOrthophoto };
}
